/**
 * 选中 AOI + 详情加载 hook。
 *
 * <p>选中 (aoiId + zipcode) → 自动拉 getAoiAttributeDetail; 切换选中自动换详情。
 * zipcode 是后端详情 / 所有变更接口的必带参数, 由 bbox item / 列表 item 携带。
 */

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { message } from 'antd';
import {
    BizAoiAttributeDetail,
    GET_AOI_ATTRIBUTE_DETAIL,
    GetAoiAttributeDetailResponse,
} from '../../aoi-platform/graphql/queries/getAoiAttributeDetail';
import { SelectedAoi } from '../types';
import { toAoiIdNumber } from '../constants';

export interface AoiSelection {
    selected: SelectedAoi | null;
    detail: BizAoiAttributeDetail | null;
    detailLoading: boolean;
    select: (next: SelectedAoi | null) => void;
    refetchDetail: () => Promise<unknown>;
}

export function useAoiSelection(): AoiSelection {
    const [selected, setSelected] = useState<SelectedAoi | null>(null);

    const { data, loading, error, refetch } = useQuery<GetAoiAttributeDetailResponse>(GET_AOI_ATTRIBUTE_DETAIL, {
        variables: selected ? { aoiId: selected.aoiId, zipcode: selected.zipcode } : undefined,
        skip: selected == null,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (error) {
            message.error(`加载 AOI 详情失败: ${error.message}`);
        }
    }, [error]);

    const select = useCallback((next: SelectedAoi | null) => {
        setSelected(next);
    }, []);

    const refetchDetail = useCallback(() => refetch(), [refetch]);

    // 切换选中时, Apollo cache-and-network 可能先回旧 AOI 的缓存, 这里按 aoiId 比对防止串数据。
    // 必须过 toAoiIdNumber: 响应里的 aoiId 运行时是字符串 (Long 标量), selected.aoiId 是数字,
    // 直接 === 永远 false, 表现为"瓦片点选后详情永远为空、抽屉残留上一个 AOI 的表单"。
    const detail = selected
        && data?.getAoiAttributeDetail != null
        && toAoiIdNumber(data.getAoiAttributeDetail.aoiId) === toAoiIdNumber(selected.aoiId)
        ? data.getAoiAttributeDetail
        : null;

    return {
        selected,
        detail,
        detailLoading: loading,
        select,
        refetchDetail,
    };
}
